import DeleteOutline from '@mui/icons-material/DeleteOutline';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/system';
import { FormDropdown } from '@src/common/components/FormDropdown';
import { TableCell } from '@src/common/components/Table';
import { TableRow } from '@src/common/components/TableRow';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import type { VehicleType } from '@src/orders/domain/AxleSummary';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { ControllerFormNumberInput } from '@src/orders/PermitFormSections/ControllerFormNumberInput';
import { ControllerFormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import { FormDimensionInput } from '@src/orders/PermitFormSections/FormDimensionInput';
import { FormWeightInput } from '@src/orders/PermitFormSections/FormWeightInput';
import { axleRowFields } from '@src/orders/schemas/AxleInformationInputsSchema';
import {
  AxleCapacityField,
  AxleFormName,
  AxleGroupField,
  AxleSpacingField,
  AxleTypeField,
  AxleWeightField,
  AxleWidthField,
  IsSteeringAxleField,
  SuspensionTypeField,
  TireCountField,
  TireRatingField,
  TireSizeField,
} from '@src/permitField/AxleInformationFields';
import type { TFunction } from 'i18next';
import type { Control, SetFieldValue, UseFormGetValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = {
  control: Control;
  axleIndex: number;
  readonly?: boolean;
  fields: string[];
  disabledFields: string[];
  vehicleType: VehicleType;
  vehiclePrefix?: string;
  /*oxlint-disable*/
  setValue: SetFieldValue<any>;
  getValue: UseFormGetValues<any>;
  /*oxlint-enable*/
  totalAxles: number;
  cumulativeAxleCount: number;
};

const calculateTabIndex = (
  columnNumber: number,
  totalAxles: number,
  axleIndexOffset: number,
  axleIndex: number,
): number => {
  return columnNumber * totalAxles + axleIndex + 1 + (axleIndexOffset - 1);
};

const All_SUPPORTED_AXLE_FIELDS_LENGTH = Object.keys(axleRowFields).length;

export const AxleTableRow = (props: Props) => {
  const { axleIndex, control, fields, totalAxles, cumulativeAxleCount } = props;
  const { t } = useTranslation();
  const pathToAxles = props.vehiclePrefix
    ? `${props.vehiclePrefix}.${AxleFormName}`
    : AxleFormName;
  const isFieldDisabled = (fieldName: string) =>
    props.disabledFields.includes(fieldName);
  const hasAtLeastOneFieldDisabled = props.fields.some(isFieldDisabled);

  //NOTE: We consider the entire table as being always fully displayed when assigning the tab index for simplicity
  //This introduces an offset when multiple axle tables are rendered.
  // eg. latest tab index from first table could be 8 while the first index of the second table could be 46
  const axleIndexOffset =
    All_SUPPORTED_AXLE_FIELDS_LENGTH * cumulativeAxleCount;

  const axleFieldPrefix =
    axleIndex !== undefined ? `${pathToAxles}.${axleIndex}` : undefined;

  const commonInputProps = {
    t,
    control,
    fieldPrefix: axleFieldPrefix,
    readonly: props.readonly,
  };
  const removeAxle = (index: number) => {
    const currentAxles = props.getValue(pathToAxles);
    const copy = [...currentAxles];
    copy.splice(index, 1);

    props.setValue(pathToAxles, copy);
  };

  return (
    <TableRow
      key={axleIndex}
      aria-label={t('axleNumber', { number: axleIndex + 1 })}
    >
      <StyledTableCell align="center" sx={{ verticalAlign: 'middle' }}>
        <Typography>{axleIndex + 1}</Typography>
      </StyledTableCell>
      <ConditionalField fields={fields} conditions={AxleTypeField.fieldName}>
        <StyledTableCell>
          <FormDropdown
            tabIndex={calculateTabIndex(
              0,
              totalAxles,
              axleIndex,
              axleIndexOffset,
            )}
            disabled={
              props.readonly || isFieldDisabled(AxleTypeField.fieldName)
            }
            fieldName={axleInformationSubFormFieldName(
              AxleTypeField.fieldName,
              axleFieldPrefix,
            )}
            options={AxleTypeField.options.map((option) => ({
              value: option,
              label: option,
            }))}
            control={control}
            ariaLabel={t(`fields.${AxleTypeField.fieldName}`)}
            label={t(`fields.${AxleTypeField.fieldName}`)}
          />
        </StyledTableCell>
      </ConditionalField>
      <ConditionalField fields={fields} conditions={AxleWeightField.fieldName}>
        <StyledTableCell>
          <FormWeightInput
            tabIndex={calculateTabIndex(
              1,
              totalAxles,
              axleIndex,
              axleIndexOffset,
            )}
            multiplyByThousand={true}
            {...metricInputProps({
              fieldName: AxleWeightField.fieldName,
              ...commonInputProps,
              disabled:
                props.readonly || isFieldDisabled(AxleWeightField.fieldName),
            })}
          />
        </StyledTableCell>
      </ConditionalField>
      <ConditionalField fields={fields} conditions={AxleSpacingField.fieldName}>
        <StyledTableCell>
          {/* Tractors never have any spacing for the first axle so the field is hidden */}
          <FormDimensionInput
            tabIndex={calculateTabIndex(
              2,
              totalAxles,
              axleIndex,
              axleIndexOffset,
            )}
            {...metricInputProps({
              fieldName: AxleSpacingField.fieldName,
              ...commonInputProps,
              disabled:
                props.readonly || isFieldDisabled(AxleSpacingField.fieldName),
            })}
            className={
              props.vehicleType === 'TRACTOR' && axleIndex === 0
                ? hidden
                : undefined
            }
          />
        </StyledTableCell>
      </ConditionalField>
      <ConditionalField fields={fields} conditions={TireCountField.fieldName}>
        <TireCountCell>
          <ControllerFormNumberInput
            tabIndex={calculateTabIndex(
              3,
              totalAxles,
              axleIndex,
              axleIndexOffset,
            )}
            disabled={
              props.readonly || isFieldDisabled(TireCountField.fieldName)
            }
            withoutArrows
            control={control}
            ariaLabel={t(`fields.${TireCountField.fieldName}`)}
            label={t(`fields.${TireCountField.fieldName}`)}
            hiddenLabel
            fieldName={axleInformationSubFormFieldName(
              TireCountField.fieldName,
              axleFieldPrefix,
            )}
          />
        </TireCountCell>
      </ConditionalField>
      <ConditionalField fields={fields} conditions={TireSizeField.fieldName}>
        <TireSizeCell>
          <FormDropdown
            tabIndex={calculateTabIndex(
              4,
              totalAxles,
              axleIndex,
              axleIndexOffset,
            )}
            disabled={
              props.readonly || isFieldDisabled(TireSizeField.fieldName)
            }
            fieldName={axleInformationSubFormFieldName(
              TireSizeField.fieldName,
              axleFieldPrefix,
            )}
            options={TireSizeField.options.map((option) => ({
              value: option,
              label: option,
            }))}
            control={control}
            ariaLabel={t(`fields.${TireSizeField.fieldName}`)}
            label={t(`fields.${TireSizeField.fieldName}`)}
          />
        </TireSizeCell>
      </ConditionalField>
      <ConditionalField fields={fields} conditions={TireRatingField.fieldName}>
        <StyledTableCell>
          <FormWeightInput
            tabIndex={calculateTabIndex(
              5,
              totalAxles,
              axleIndex,
              axleIndexOffset,
            )}
            multiplyByThousand={true}
            {...metricInputProps({
              fieldName: TireRatingField.fieldName,
              ...commonInputProps,
              disabled:
                props.readonly || isFieldDisabled(TireRatingField.fieldName),
            })}
          />
        </StyledTableCell>
      </ConditionalField>
      <ConditionalField
        fields={fields}
        conditions={AxleCapacityField.fieldName}
      >
        <StyledTableCell>
          <FormWeightInput
            tabIndex={calculateTabIndex(
              6,
              totalAxles,
              axleIndex,
              axleIndexOffset,
            )}
            multiplyByThousand={true}
            {...metricInputProps({
              fieldName: AxleCapacityField.fieldName,
              ...commonInputProps,
              disabled:
                props.readonly || isFieldDisabled(AxleCapacityField.fieldName),
            })}
          />
        </StyledTableCell>
      </ConditionalField>
      <ConditionalField fields={fields} conditions={AxleWidthField.fieldName}>
        <StyledTableCell>
          <FormDimensionInput
            tabIndex={calculateTabIndex(
              7,
              totalAxles,
              axleIndex,
              axleIndexOffset,
            )}
            {...metricInputProps({
              fieldName: AxleWidthField.fieldName,
              ...commonInputProps,
              disabled:
                props.readonly || isFieldDisabled(AxleWidthField.fieldName),
            })}
          />
        </StyledTableCell>
      </ConditionalField>
      <ConditionalField fields={fields} conditions={AxleGroupField.fieldName}>
        <StyledTableCell>
          <FormDropdown
            tabIndex={calculateTabIndex(
              8,
              totalAxles,
              axleIndex,
              axleIndexOffset,
            )}
            disabled={
              props.readonly || isFieldDisabled(AxleGroupField.fieldName)
            }
            fieldName={axleInformationSubFormFieldName(
              AxleGroupField.fieldName,
              axleFieldPrefix,
            )}
            options={AxleGroupField.options.map((option) => ({
              value: option,
              label: option,
            }))}
            control={control}
            ariaLabel={t(`fields.${AxleGroupField.fieldName}`)}
            label={t(`fields.${AxleGroupField.fieldName}`)}
          />
        </StyledTableCell>
      </ConditionalField>
      <ConditionalField
        fields={fields}
        conditions={SuspensionTypeField.fieldName}
      >
        <StyledTableCell>
          <FormDropdown
            tabIndex={calculateTabIndex(
              9,
              totalAxles,
              axleIndex,
              axleIndexOffset,
            )}
            disabled={
              props.readonly || isFieldDisabled(SuspensionTypeField.fieldName)
            }
            fieldName={axleInformationSubFormFieldName(
              SuspensionTypeField.fieldName,
              axleFieldPrefix,
            )}
            options={SuspensionTypeField.options.map((option) => ({
              value: option,
              label: option,
            }))}
            control={control}
            ariaLabel={t(`fields.${SuspensionTypeField.fieldName}`)}
            label={t(`fields.${SuspensionTypeField.fieldName}`)}
          />
        </StyledTableCell>
      </ConditionalField>
      <ConditionalField
        fields={fields}
        conditions={IsSteeringAxleField.fieldName}
      >
        <CheckboxTableCell>
          <ControllerFormCheckbox
            tabIndex={calculateTabIndex(
              10,
              totalAxles,
              axleIndex,
              axleIndexOffset,
            )}
            disabled={
              props.readonly || isFieldDisabled(IsSteeringAxleField.fieldName)
            }
            fieldName={axleInformationSubFormFieldName(
              IsSteeringAxleField.fieldName,
              axleFieldPrefix,
            )}
            control={control}
            ariaLabel={t(`fields.${IsSteeringAxleField.fieldName}`)}
            label={t(`fields.${IsSteeringAxleField.fieldName}`)}
          />
        </CheckboxTableCell>
      </ConditionalField>
      <StyledTableCell>
        <IconButton
          aria-label={t('removeAxle')}
          onClick={() => removeAxle(axleIndex)}
          disabled={hasAtLeastOneFieldDisabled}
        >
          <DeleteOutline />
        </IconButton>
      </StyledTableCell>
    </TableRow>
  );
};

const metricInputProps = ({
  t,
  control,
  fieldName,
  fieldPrefix,
  readonly,
  disabled,
}: {
  t: TFunction;
  control: Control;
  fieldName: string;
  fieldPrefix?: string;
  readonly?: boolean;
  disabled?: boolean;
}) => ({
  disabled: readonly || disabled,
  label: '',
  fieldName,
  type: 'text',
  name: axleInformationSubFormFieldName(fieldName, fieldPrefix),
  control,
  displayUnits: false,
  ariaLabel: t(`fields.${fieldName}`),
});

const axleInformationSubFormFieldName = (
  formFieldName: string,
  prefix?: string,
) => {
  return prefix ? `${prefix}.${formFieldName}` : formFieldName;
};

const StyledTableCell = styled(TableCell)`
  padding: ${theme.spacing['2']} ${theme.spacing['1']};
  vertical-align: middle;

  & > div {
    min-width: 80px;
    width: 100%;
  }
`;
const TireSizeCell = styled(TableCell)`
  padding: ${theme.spacing['2']} ${theme.spacing['1']};
  vertical-align: top;

  & > div {
    min-width: 200px;
    width: 100%;
  }
`;
const TireCountCell = styled(TableCell)`
  padding: ${theme.spacing['2']} ${theme.spacing['1']};
  vertical-align: top;

  & > div {
    min-width: 50px;
    width: 100%;
  }
`;

const CheckboxTableCell = styled(TableCell)`
  & > div {
    min-width: 80px;
    width: 100%;
    align-items: center;
  }
`;

const hidden = css({
  visibility: 'hidden',
});
